import { useState, useEffect } from 'react';
import { getGalleriBySlug } from '../lib/cms';
import { imageUrl } from '../lib/images';
import Lightbox from './Lightbox';

export default function GallerySection({ slug, title, subtitle, className = '' }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadGallery() {
      setLoading(true);
      const data = await getGalleriBySlug(slug);
      if (mounted) {
        setGallery(data);
        setLoading(false);
      }
    }
    loadGallery();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const images = gallery?.billeder || [];

  const handleOpenLightbox = (index) => {
    setActiveImageIndex(index);
    setIsLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className={`py-16 text-center ${className}`}>
        <div className="inline-flex items-center gap-3 text-zinc-400 text-sm">
          <i className="fa-solid fa-circle-notch animate-spin text-[#B89B72]" aria-hidden="true" />
          <span>Henter galleri fra CMS...</span>
        </div>
      </div>
    );
  }

  if (!gallery || images.length === 0) {
    return null;
  }

  return (
    <section className={`py-16 md:py-24 ${className}`} aria-label={title || gallery.titel}>
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-medium text-[#F4EFEA] tracking-tight">
            {title || gallery.titel}
          </h2>
          {gallery.beskrivelse && (
            <div
              className="mt-4 text-zinc-400 text-sm md:text-base leading-relaxed space-y-2"
              dangerouslySetInnerHTML={{ __html: gallery.beskrivelse }}
            />
          )}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {images.map((imgUrl, idx) => {
            return (
              <div
                key={idx}
                onClick={() => handleOpenLightbox(idx)}
                className="group relative aspect-[4/5] rounded-none overflow-hidden bg-zinc-900 cursor-pointer border border-white/5 transition-all duration-300 hover:border-[#B89B72]/40"
              >
                <img
                  src={imageUrl(imgUrl, 'width=700,quality=85,format=auto')}
                  alt={`${gallery.titel} foto ${idx + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />

                {/* Subtle dark gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
                  <div className="flex items-center justify-between w-full">
                    <span className="text-xs uppercase tracking-wider text-white font-medium">
                      Vis forstørret
                    </span>
                    <div className="w-8 h-8 rounded-none bg-white/20 backdrop-blur-md flex items-center justify-center text-white text-xs">
                      <i className="fa-solid fa-expand" aria-hidden="true" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox Modal */}
      <Lightbox
        images={images}
        currentIndex={activeImageIndex}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        onNavigate={(nextIdx) => setActiveImageIndex(nextIdx)}
      />
    </section>
  );
}
